import { CONNECTION_PROVIDER_INFO, CONNECTION_STATUS_LABELS, type ConnectionDto } from '@shared/connections';
import type { ConnectionProviderId } from '@shared/sources';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Lock } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Button } from '../../components/ui/button';
import { Alert, Badge } from '../../components/ui/feedback';
import { Field, Input, Textarea } from '../../components/ui/form';
import { api } from '../../lib/api';
import { queryKeys } from '../../lib/query';
import { errorMessage, fieldErrors } from '../../lib/session';

const STATUS_TONE = {
  connected: 'success',
  invalid: 'danger',
  expired: 'warning',
  revoked: 'neutral',
} as const;

function formatDate(value: number): string {
  return new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export interface ConnectionControlProps {
  provider: ConnectionProviderId;
  connection: ConnectionDto | undefined;
  /** Writing a connection is admin-only; members see the state without a dead button. */
  canManage: boolean;
}

/** Credentials that are documents rather than single-line keys. */
const MULTILINE_PROVIDERS: ReadonlySet<ConnectionProviderId> = new Set(['google_sheets', 'gmail']);

/**
 * Add or remove the credential a source needs. The secret is posted once and never read back —
 * after saving, only the provider's own hint is displayed.
 */
export function ConnectionControl({ provider, connection, canManage }: ConnectionControlProps) {
  const info = CONNECTION_PROVIDER_INFO[provider];
  const multiline = MULTILINE_PROVIDERS.has(provider);
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [secret, setSecret] = useState('');
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  const refresh = async (): Promise<void> => {
    await Promise.all([
      client.invalidateQueries({ queryKey: queryKeys.connections }),
      client.invalidateQueries({ queryKey: queryKeys.catalogSources }),
    ]);
  };

  const save = useMutation({
    mutationFn: (value: string) => api.put<ConnectionDto>('/api/connections', { provider, secret: value }),
    onSuccess: async () => {
      setSecret('');
      setOpen(false);
      await refresh();
    },
  });

  const remove = useMutation({
    mutationFn: () => api.delete<{ ok: true }>(`/api/connections/${provider}`),
    onSuccess: async () => {
      setConfirmingRemove(false);
      await refresh();
    },
  });

  const errors = fieldErrors(save.error);

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    save.mutate(secret);
  };

  if (connection) {
    return (
      <div className="rounded-[var(--radius-panel)] border border-border bg-muted/40 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <KeyRound className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <span className="text-[13px] font-medium text-foreground">{info.label}</span>
            <span className="tabular text-[13px] text-muted-foreground">{connection.hint ?? 'stored'}</span>
            <Badge tone={STATUS_TONE[connection.status]}>{CONNECTION_STATUS_LABELS[connection.status]}</Badge>
          </div>
          {canManage ? (
            confirmingRemove ? (
              <span className="flex items-center gap-2">
                <Button size="sm" variant="destructive" loading={remove.isPending} onClick={() => remove.mutate()}>
                  Confirm disconnect
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmingRemove(false)}>
                  Cancel
                </Button>
              </span>
            ) : (
              <Button size="sm" variant="outline" onClick={() => setConfirmingRemove(true)}>
                Disconnect
              </Button>
            )
          ) : null}
        </div>
        <p className="mt-1.5 text-[13px] text-muted-foreground">
          {connection.lastVerifiedAt
            ? `Last used successfully on ${formatDate(connection.lastVerifiedAt)}.`
            : 'Saved but not verified yet — the first run will confirm whether it works.'}
        </p>
        {connection.lastError ? (
          <Alert className="mt-2" tone="error">
            {connection.lastError}
          </Alert>
        ) : null}
        {remove.error ? (
          <Alert className="mt-2" tone="error">
            {errorMessage(remove.error)}
          </Alert>
        ) : null}
      </div>
    );
  }

  if (!canManage) {
    return (
      <p className="text-[13px] text-muted-foreground">
        {info.label} is not connected. An administrator of this workspace can add it.
      </p>
    );
  }

  if (!open) {
    return (
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        Add {info.label}
      </Button>
    );
  }

  return (
    <form className="space-y-3 rounded-[var(--radius-panel)] border border-border bg-muted/40 p-3" onSubmit={onSubmit} noValidate>
      {save.error ? <Alert tone="error">{errorMessage(save.error)}</Alert> : null}
      <Field label={info.label} hint={info.help} error={errors['secret']} required>
        {({ id, describedBy, invalid }) =>
          // A service account key is a multi-line JSON document: a one-line masked box makes it
          // impossible to see whether the paste worked. It is still write-only — saving clears
          // it, and it is never sent back to the browser afterwards.
          multiline ? (
            <Textarea
              id={id}
              className="min-h-28 font-mono text-[12px]"
              autoComplete="off"
              spellCheck={false}
              placeholder={info.placeholder}
              value={secret}
              invalid={invalid}
              aria-describedby={describedBy}
              onChange={(event) => setSecret(event.target.value)}
              autoFocus
              required
            />
          ) : (
            <Input
              id={id}
              type="password"
              autoComplete="off"
              spellCheck={false}
              placeholder={info.placeholder}
              value={secret}
              invalid={invalid}
              aria-describedby={describedBy}
              onChange={(event) => setSecret(event.target.value)}
              autoFocus
              required
            />
          )
        }
      </Field>
      <p className="flex items-start gap-1.5 text-[13px] text-muted-foreground">
        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        Encrypted with AES-256-GCM before it is stored, scoped to this workspace, and never shown again.
      </p>
      <div className="flex items-center gap-2">
        <Button type="submit" size="sm" loading={save.isPending} disabled={secret.trim().length === 0}>
          Save connection
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => {
            setSecret('');
            setOpen(false);
          }}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}
